// TENNIS BRIDGE-2 — useTennisProduct hook.
//
// Reads a tennis product's latest published output from the Supabase pool
// (tennis_product_picks) via the get_tennis_product RPC. The strikza-tim engine
// publishes into that pool (Bridge-3); this hook is the web read path that lets
// each standalone product screen render real picks.
//
// HONEST-EMPTY: when nothing is published the RPC returns { found: false } and
// the hook returns found=false with an empty picks array — the screen then
// shows its awaiting-data panel. No fabrication, ever.

import { useCallback, useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../../../supabase/browser.ts";
import type { ProductFormat } from "./registry.ts";

// One normalized pick — tolerant superset across all product formats. A board
// pick uses rank; a tiers pick uses tier; an acca leg uses all the odds fields;
// an alert uses fired_at / note. Unused fields are simply null.
export interface TennisPick {
  match_name: string;
  tournament: string | null;
  round: string | null;
  market: string;
  selection: string;
  book_odds: number | null;
  edge_pct: number | null;
  // Running CLV: the entry-anchor price we first recorded today + clvPct(entry,
  // current). A live preview of realized post-match CLV; null until the line moves.
  entry_odds: number | null;
  clv_pct: number | null;
  confidence: string | null;
  rank: number | null;
  tier: string | null;
  fair_prob: number | null;
  markets: TennisPickMarket[] | null;
  kickoff_at: string | null;
  note: string | null;
}

// One market read inside a match (the Fair Price drawer detail).
export interface TennisPickMarket {
  market: string;
  selection: string | null;
  fair_prob: number | null;
  fair_odds: number | null;
  book_odds: number | null;
  edge_pct: number | null;
  entry_odds: number | null;
  clv_pct: number | null;
  tier: string | null;
}

// Headline summary (acca combined odds, board count, etc). All optional.
export interface TennisProductHeadline {
  combined_odds: number | null;
  leg_count: number | null;
  count: number | null;
}

export interface UseTennisProductState {
  found: boolean;
  format: ProductFormat | null;
  tournament: string | null;
  pickDate: string | null;
  publishedAt: string | null;
  headline: TennisProductHeadline | null;
  picks: TennisPick[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// ─── Parsers ──────────────────────────────────────────────
function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}
function str(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

function parsePick(raw: Record<string, unknown>): TennisPick {
  return {
    match_name: String(raw.match_name ?? raw.match ?? ""),
    tournament: str(raw.tournament),
    round: str(raw.round),
    market: String(raw.market ?? raw.market_code ?? ""),
    selection: String(raw.selection ?? raw.pick ?? ""),
    book_odds: num(raw.book_odds ?? raw.odds),
    edge_pct: num(raw.edge_pct ?? raw.edge),
    entry_odds: num(raw.entry_odds),
    clv_pct: num(raw.clv_pct),
    confidence: str(raw.confidence),
    rank: num(raw.rank),
    tier: str(raw.tier),
    fair_prob: num(raw.fair_prob),
    markets: parseMarkets(raw.markets),
    kickoff_at: str(raw.kickoff_at ?? raw.match_date),
    note: str(raw.note ?? raw.reason),
  };
}

function parseMarkets(raw: unknown): TennisPickMarket[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  return raw.map((m) => {
    const mk = (m ?? {}) as Record<string, unknown>;
    return {
      market: String(mk.market ?? mk.market_code ?? ""),
      selection: str(mk.selection),
      fair_prob: num(mk.fair_prob),
      fair_odds: num(mk.fair_odds),
      book_odds: num(mk.book_odds),
      edge_pct: num(mk.edge_pct),
      entry_odds: num(mk.entry_odds),
      clv_pct: num(mk.clv_pct),
      tier: str(mk.tier),
    };
  });
}

// ─── Defensive fixture de-duplication ─────────────────────
// No product should ever show the same fixture twice. The engine can publish
// a fixture under two ids (an odds-api hash + a web-draw `web_…` id) — the
// engine-side collapse only applies on its NEXT publish, so we also guard here
// at the read layer: same canonical (order-independent) player pair → one pick,
// keeping the better copy (a priced/bettable line beats "no line", then higher
// edge). Permanent protection regardless of what's in the pool.
export function canonicalFixtureKey(matchName: string): string {
  const norm = (s: string): string =>
    s.toLowerCase().normalize("NFD").replace(/[^a-z\s]/g, " ").replace(/\s+/g, " ").trim();
  const [a, b] = String(matchName || "").split(/\s+vs\.?\s+/i);
  const key = [norm(a ?? ""), norm(b ?? "")].sort().join(" v ");
  return key === " v " ? "" : key;
}

export function dedupeByFixture(picks: TennisPick[]): TennisPick[] {
  const score = (p: TennisPick): number =>
    (p.book_odds != null ? 1e6 : 0) + (p.edge_pct ?? -1e5);
  const best = new Map<string, TennisPick>();
  const order: string[] = [];
  for (const p of picks) {
    const key = canonicalFixtureKey(p.match_name) || `__${order.length}`;
    const cur = best.get(key);
    if (!cur) {
      best.set(key, p);
      order.push(key);
    } else if (score(p) > score(cur)) {
      best.set(key, p);
    }
  }
  return order.map((k) => best.get(k)!);
}

// Drop picks for matches that have already kicked off — a started game is no
// longer a pre-match pick and shouldn't sit on a live product board. Picks with
// no kickoff time are kept (we can't prove they've started; honest). A small
// grace window absorbs clock skew. Covers EVERY tennis product (all read here).
export function dropStartedPicks(picks: TennisPick[], graceMin = 0): TennisPick[] {
  const cutoff = Date.now() - graceMin * 60_000;
  return picks.filter((p) => {
    if (!p.kickoff_at) return true;
    const t = Date.parse(p.kickoff_at);
    return Number.isNaN(t) ? true : t >= cutoff;
  });
}

function parseHeadline(raw: unknown): TennisProductHeadline | null {
  if (!raw || typeof raw !== "object") return null;
  const h = raw as Record<string, unknown>;
  return {
    combined_odds: num(h.combined_odds),
    leg_count: num(h.leg_count),
    count: num(h.count),
  };
}

// ─── Hook ─────────────────────────────────────────────────
export function useTennisProduct(slug: string, date?: string | null): UseTennisProductState {
  const [state, setState] = useState<Omit<UseTennisProductState, "refetch">>({
    found: false,
    format: null,
    tournament: null,
    pickDate: null,
    publishedAt: null,
    headline: null,
    picks: [],
    loading: true,
    error: null,
  });
  const [tick, setTick] = useState(0);
  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!slug) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    setState((s) => ({ ...s, loading: true, error: null }));

    void (async () => {
      try {
        const args: Record<string, unknown> = { p_slug: slug };
        if (typeof date === "string" && date) args.p_date = date;
        const { data, error: rpcError } = await (
          client.rpc as unknown as (fn: string, a?: Record<string, unknown>) => Promise<{
            data: Record<string, unknown> | null;
            error: { message: string } | null;
          }>
        )("get_tennis_product", args);

        if (cancelled) return;

        if (rpcError) {
          setState((s) => ({ ...s, loading: false, error: rpcError.message }));
          return;
        }

        const raw = (data ?? {}) as Record<string, unknown>;
        if (raw.found !== true) {
          setState({
            found: false,
            format: null,
            tournament: null,
            pickDate: null,
            publishedAt: null,
            headline: null,
            picks: [],
            loading: false,
            error: null,
          });
          return;
        }

        const payload = (raw.payload && typeof raw.payload === "object" ? raw.payload : {}) as Record<string, unknown>;
        const rawPicks = Array.isArray(payload.picks) ? (payload.picks as Record<string, unknown>[]) : [];

        setState({
          found: true,
          format: (str(raw.format) as ProductFormat | null) ?? null,
          tournament: str(raw.tournament),
          pickDate: str(raw.pick_date),
          publishedAt: str(raw.published_at),
          headline: parseHeadline(payload.headline),
          picks: dropStartedPicks(
            str(raw.format) === "acca" ? rawPicks.map(parsePick) : dedupeByFixture(rawPicks.map(parsePick)),
          ),
          loading: false,
          error: null,
        });
      } catch (e) {
        if (cancelled) return;
        setState((s) => ({ ...s, loading: false, error: e instanceof Error ? e.message : String(e) }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug, date, tick]);

  return useMemo(() => ({ ...state, refetch }), [state, refetch]);
}
